import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type GoogleCalendarLink = components["schemas"]["GoogleCalendarLinkView"];

export const GOOGLE_CALENDAR_KEY = ["me", "calendar", "google"] as const;

/**
 * The signed-in person's OWN Google Calendar link (2026-10-09). Person-scoped — the same answer in the artist and
 * the roster shell, whichever band is open — because the calendar holds the shows this person is on, across every
 * band.
 *
 * While the link is connected but its calendar does not exist yet (the worker is creating it), it polls briefly so
 * the section can stop saying "Setting up…" on its own.
 */
export function useGoogleCalendar() {
  const { api } = useApp();
  return useQuery({
    queryKey: GOOGLE_CALENDAR_KEY,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/calendar/google");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    refetchInterval: (q) => (q.state.data?.state === "connected" && !q.state.data.calendarReady ? 3_000 : false),
    staleTime: 30_000,
  });
}

/** Begin the connect: returns Google's consent URL for the caller to send the popup (or the page) to. */
export function useStartGoogleCalendar() {
  const { api } = useApp();
  return useMutation({
    mutationFn: async ({ returnUrl }: { returnUrl: string }) => {
      const { data, error, response } = await api.POST("/api/v1/me/calendar/google/start", {
        body: { returnUrl },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onError: (err) => pushToast(errorMessage(err, "Couldn't start the Google connection"), "error"),
  });
}

/** Disconnect: the server deletes the "Artist CNTRL" calendar and revokes the grant. */
export function useDisconnectGoogleCalendar() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE("/api/v1/me/calendar/google");
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => pushToast("Google Calendar disconnected.", "info"),
    onError: (err) => pushToast(errorMessage(err, "Couldn't disconnect Google Calendar"), "error"),
    onSettled: () => void qc.invalidateQueries({ queryKey: GOOGLE_CALENDAR_KEY }),
  });
}
